import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmailSectionLayout } from '../../components/EmailSectionLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Loader2, Send, CheckCircle2, Clock, XCircle, RefreshCw, MailCheck, Eye, MousePointerClick, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const SEND_TINT = {
  sent: 'text-emerald-700 bg-emerald-100',
  delivered: 'text-emerald-700 bg-emerald-100',
  pending: 'text-amber-700 bg-amber-100',
  failed: 'text-red-700 bg-red-100',
};

export const EmailCampaignStatsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, sd] = await Promise.all([
        api.get(`/email/campaigns/${id}/stats`),
        api.get(`/email/campaigns/${id}/sends`),
      ]);
      setData(s.data);
      setSends(sd.data?.sends || []);
    } catch { toast.error('Could not load stats'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const counts = data?.counts || {};
  const sentTotal = (counts.sent || 0) + (counts.delivered || 0) + (counts.opened || 0) + (counts.clicked || 0);

  return (
    <EmailSectionLayout
      actions={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => navigate('/email/campaigns')}><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
          <Button variant="outline" onClick={load} data-testid="refresh-stats-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
        </div>
      }
    >
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-bold text-zinc-900" data-testid="stats-campaign-name">{data?.campaign?.name}</h2>
            <p className="text-sm text-zinc-500">{data?.campaign?.subject}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatBox icon={Send} label="Total" value={counts.total || 0} testId="stat-total" tint="bg-indigo-100 text-indigo-600" />
            <StatBox icon={CheckCircle2} label="Sent" value={sentTotal} testId="stat-sent" tint="bg-emerald-100 text-emerald-600" />
            <StatBox icon={Clock} label="Pending" value={counts.pending || 0} testId="stat-pending" tint="bg-amber-100 text-amber-600" />
            <StatBox icon={XCircle} label="Failed" value={counts.failed || 0} testId="stat-failed" tint="bg-red-100 text-red-600" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatBox icon={MailCheck} label="Delivered" value={counts.delivered || 0} testId="stat-delivered" tint="bg-sky-100 text-sky-600" />
            <StatBox icon={Eye} label="Opened" value={counts.opened || 0} testId="stat-opened" tint="bg-violet-100 text-violet-600" />
            <StatBox icon={MousePointerClick} label="Clicked" value={counts.clicked || 0} testId="stat-clicked" tint="bg-fuchsia-100 text-fuchsia-600" />
            <StatBox icon={AlertTriangle} label="Bounced" value={counts.bounced || 0} testId="stat-bounced" tint="bg-orange-100 text-orange-600" />
          </div>

          <Card data-testid="sends-table">
            <CardContent className="p-0">
              {sends.length === 0 ? (
                <div className="py-12 text-center text-zinc-500 text-sm">No sends yet. Send or schedule this campaign to see per-recipient status.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-zinc-200 text-left text-zinc-500">
                      <th className="px-4 py-3 font-medium">Recipient</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Sent at</th>
                      <th className="px-4 py-3 font-medium">Error</th>
                    </tr></thead>
                    <tbody>
                      {sends.map((s) => (
                        <tr key={s.id} className="border-b border-zinc-100" data-testid={`send-row-${s.id}`}>
                          <td className="px-4 py-2.5 text-zinc-800">{s.recipient_email}</td>
                          <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEND_TINT[s.delivery_status] || 'bg-zinc-100 text-zinc-600'}`}>{s.delivery_status}</span></td>
                          <td className="px-4 py-2.5 text-zinc-500">{s.sent_at ? new Date(s.sent_at).toLocaleString() : '—'}</td>
                          <td className="px-4 py-2.5 text-red-500 text-xs max-w-[240px] truncate">{s.error || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </EmailSectionLayout>
  );
};

const StatBox = ({ icon: Icon, label, value, testId, tint }) => (
  <Card data-testid={testId}><CardContent className="p-5">
    <div className={`w-9 h-9 rounded-lg ${tint} flex items-center justify-center mb-3`}><Icon className="w-4 h-4" /></div>
    <div className="text-2xl font-bold text-zinc-900">{value}</div>
    <div className="text-sm text-zinc-500">{label}</div>
  </CardContent></Card>
);

export default EmailCampaignStatsPage;
